function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useContext, useMemo } from 'react';
import { Button, Row, Col, Form, InputGroup, Table, Dropdown } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import { EXPRESSION_LABEL, FORMULA, CONTEXT_FORMULA, TERM, UNSELECTED } from "../constants";
import FontAwesome from 'react-fontawesome';
import LockButton from '../buttons/LockButton';
import Help from "../buttons/Help";
import AddButton from "../buttons/AddButton";
import HenkinHintikkaGameButton from "../buttons/HenkinHintikkaGameButton";
import HenkinHintikkaGameContainer from "../redux/containers/HenkinHintikkaGameContainer";
import { LogicContext } from '../logicContext';
const helpFormula = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", null, "The truth of closed first-order formulas in the structure\xA0\uD835\uDCDC and the satisfaction of open first-order formulas by the valuation of variables\xA0\uD835\uDC52 in\xA0\uD835\uDCDC can be examined in this section."), /*#__PURE__*/React.createElement("p", null, "The desired/expected truth or satisfaction can be selected from the \u22A8/\u22AD menu below a formula. Structure Explorer checks the correctness of your selection."), /*#__PURE__*/React.createElement("p", {
  className: "mb-0"
}, "Syntactic requirements:"), /*#__PURE__*/React.createElement("ul", {
  className: "mb-0"
}, /*#__PURE__*/React.createElement("li", null, "All non-logical symbols used in formulas must come from the language \uD835\uDCDB and must be used according to their type and arity. All other alphanumerical symbols are treated as variables."), /*#__PURE__*/React.createElement("li", null, "Formulas must be properly parenthesized."), /*#__PURE__*/React.createElement("li", null, "The following notation of logical symbols is accepted:", /*#__PURE__*/React.createElement(Table, {
  size: "sm",
  striped: true,
  className: "my-2 border-bottom"
}, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Symbol"), /*#__PURE__*/React.createElement("th", null, "Notation"))), /*#__PURE__*/React.createElement("tbody", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Equality"), /*#__PURE__*/React.createElement("td", null, " =, \u2250")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Non-equality"), /*#__PURE__*/React.createElement("td", null, " !=, ", '<>', ", /=, \u2260")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Negation"), /*#__PURE__*/React.createElement("td", null, " \\neg, \\lnot, -, !, ~, \xAC")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Conjunction"), /*#__PURE__*/React.createElement("td", null, " \\wedge, \\land, &&, &, /\\, \u2227")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Disjunction"), /*#__PURE__*/React.createElement("td", null, " \\vee, \\lor, ||, |, \\/, \u2228")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Implication"), /*#__PURE__*/React.createElement("td", null, " \\to, \\limpl, ", '->', ", \u2192")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Equivalence"), /*#__PURE__*/React.createElement("td", null, " \\lequiv, \\leftrightarrow, ", '<->', ", \u2194\uFE0E")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "Existential quantifier"), /*#__PURE__*/React.createElement("td", null, " \\exists, \\e, \\E, \u2203")), /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", null, "General quantifier"), /*#__PURE__*/React.createElement("td", null, " \\forall, \\a, \\A, \u2200"))))), /*#__PURE__*/React.createElement("li", null, "The priority of logical symbols:", /*#__PURE__*/React.createElement("ol", {
  className: "my-0"
}, /*#__PURE__*/React.createElement("li", null, "\u2250, \u2260 (highest priority)"), /*#__PURE__*/React.createElement("li", null, "\xAC, \u2200, \u2203"), /*#__PURE__*/React.createElement("li", null, "\u2227 (left-associative, i.e., A \u2227 B \u2227 C \u2261 ((A \u2227 B) \u2227 C))"), /*#__PURE__*/React.createElement("li", null, "\u2228 (left-associative)"), /*#__PURE__*/React.createElement("li", null, "\u2192 (right-associative, i.e., A \u2192 B \u2192 C \u2261 (A \u2192 (B \u2192 C)))"), /*#__PURE__*/React.createElement("li", null, "\u2194\uFE0E (non-associative, lowest priority)")))));
const helpTerm = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", null, "Denotations of first-order terms in the structure \uD835\uDCDC for the valuation of variables \uD835\uDC52 can be examined in this section."), /*#__PURE__*/React.createElement("p", null, "The desired/expected denotation can be selected from the menu below the term. Structure Explorer checks the correctness of your selection."), /*#__PURE__*/React.createElement("p", {
  className: "mb-0"
}, "Syntactic requirements:"), /*#__PURE__*/React.createElement("ul", {
  className: "mb-0"
}, /*#__PURE__*/React.createElement("li", null, "All individual constants and function symbols used in the terms must come from the language \uD835\uDCDB and must be used according to their type and arity. All other alphanumerical symbols are treated as variables."), /*#__PURE__*/React.createElement("li", null, "Terms must be properly parenthesized.")));

const getFormulaAnswers = () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("option", {
  key: 'unselected',
  value: ''
}, "\u22A8/\u22AD?"), /*#__PURE__*/React.createElement("option", {
  key: 'true',
  value: 'true'
}, "\u22A8"), /*#__PURE__*/React.createElement("option", {
  key: 'false',
  value: 'false'
}, "\u22AD"));

const getTermAnswers = domain => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("option", {
  value: ''
}, "Select a domain element\u2026"), domain.map(item => /*#__PURE__*/React.createElement("option", {
  key: item,
  value: item
}, item)));

function prepareExpressions(formulas, terms) {
  let f = {
    items: formulas,
    expressionType: FORMULA,
    answers: () => getFormulaAnswers(),
    help: helpFormula,
    panelTitle: 'Truth of formulas in 𝓜'
  };
  let t = {
    items: terms,
    expressionType: TERM,
    answers: domain => getTermAnswers(domain),
    help: helpTerm,
    panelTitle: 'Denotation of terms in 𝓜'
  };
  return [f, t];
}

const Expressions = props => {
  const [showHelp, setShowHelp] = useState({
    [FORMULA]: false,
    [TERM]: false
  });

  const onToggle = exprType => newValue => setShowHelp(sh => ({ ...sh,
    [exprType]: newValue
  }));

  const context = useContext(LogicContext);
  const noCtxFormulas = context?.axioms.length === 0 && context?.formulas.length === 0;
  return /*#__PURE__*/React.createElement(React.Fragment, null, prepareExpressions(props.formulas, props.terms).map(expression => /*#__PURE__*/React.createElement(Card, {
    className: "mb-3",
    key: expression.expressionType
  }, /*#__PURE__*/React.createElement(Card.Header, {
    as: "h5",
    className: "d-flex justify-content-between"
  }, /*#__PURE__*/React.createElement("span", null, expression.panelTitle), /*#__PURE__*/React.createElement(Help, {
    subject: expression.expressionType.toLowerCase(),
    show: showHelp[expression.expressionType],
    onToggle: onToggle([expression.expressionType])
  }, expression.help)), /*#__PURE__*/React.createElement(Card.Body, null, expression.items.map((item, index) => /*#__PURE__*/React.createElement(ExpressionItem, _extends({}, props, {
    expression: expression,
    item: item,
    index: index
  }))), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
    xs: "auto"
  }, /*#__PURE__*/React.createElement(AddButton, {
    onClickAddFunction: props.addExpression,
    addType: expression.expressionType
  })), expression.expressionType === FORMULA && context && /*#__PURE__*/React.createElement(Col, {
    xs: "auto"
  }, /*#__PURE__*/React.createElement(Dropdown, null, /*#__PURE__*/React.createElement(Dropdown.Toggle, {
    disabled: noCtxFormulas,
    variant: "success"
  }, /*#__PURE__*/React.createElement(FontAwesome, {
    name: "plus"
  }), " \xA0Add from context"), /*#__PURE__*/React.createElement(Dropdown.Menu, null, context.axioms.map(f => /*#__PURE__*/React.createElement(Dropdown.Item, {
    key: f.name,
    title: f.formula,
    onClick: () => props.addExpression(CONTEXT_FORMULA, {
      name: f.name,
      type: 'axiom'
    })
  }, "Axiom: ", f.name)), context.formulas.map(f => /*#__PURE__*/React.createElement(Dropdown.Item, {
    key: f.name,
    onClick: () => props.addExpression(CONTEXT_FORMULA, {
      name: f.name,
      type: 'named_formula'
    })
  }, "Named formula: ", f.name))))))))));
};

function ExpressionItem(props) {
  const {
    item,
    index,
    expression
  } = props;
  const contextFormula = useContext(LogicContext)?.getFormula(item.contextInfo?.name);
  const isContextFormula = item.contextInfo !== undefined; // indicates that formula was possibly deleted from context

  const isMissing = isContextFormula && !contextFormula; // update formula value when its value in context changes

  useMemo(() => {
    contextFormula && props.onInputChange(contextFormula.formula, index, FORMULA, true);
  }, [contextFormula?.formula]);
  return /*#__PURE__*/React.createElement(Form, {
    key: "expression-form-" + index
  }, /*#__PURE__*/React.createElement(Form.Group, null, /*#__PURE__*/React.createElement(InputGroup, {
    size: "sm",
    className: "mb-1 has-validation"
  }, /*#__PURE__*/React.createElement(InputGroup.Prepend, null, /*#__PURE__*/React.createElement(InputGroup.Text, {
    id: expression.expressionType.toLowerCase() + '-' + index
  }, isContextFormula ? item.contextInfo.name : /*#__PURE__*/React.createElement(React.Fragment, null, EXPRESSION_LABEL[expression.expressionType], /*#__PURE__*/React.createElement("sub", null, index + 1)))), /*#__PURE__*/React.createElement(Form.Control, {
    type: "text",
    value: item.value,
    onChange: e => props.onInputChange(e.target.value, index, expression.expressionType),
    id: expression.expressionType.toLowerCase() + '-' + index,
    disabled: isContextFormula || item.inputLocked,
    isInvalid: item.errorMessage.length > 0 || isMissing,
    onFocus: () => {
      props.diagramModel.clearSelection();
    }
  }), /*#__PURE__*/React.createElement(InputGroup.Append, null, /*#__PURE__*/React.createElement(Button, {
    variant: "outline-danger",
    onClick: () => props.removeExpression(expression.expressionType, index)
  }, /*#__PURE__*/React.createElement(FontAwesome, {
    name: "fas fa-trash"
  })), props.teacherMode ? /*#__PURE__*/React.createElement(LockButton, {
    lockFn: () => props.lockExpressionValue(expression.expressionType, index),
    locked: item.inputLocked
  }) : null), /*#__PURE__*/React.createElement(Form.Control.Feedback, {
    type: "invalid"
  }, `${isMissing ? 'Formula is missing in context. ' : ''}${item.errorMessage}`)), /*#__PURE__*/React.createElement(Form.Row, {
    className: "align-items-center"
  }, /*#__PURE__*/React.createElement(Col, {
    xs: "auto",
    className: "mb-1"
  }, /*#__PURE__*/React.createElement(InputGroup, {
    size: "sm"
  }, /*#__PURE__*/React.createElement(InputGroup.Prepend, null, /*#__PURE__*/React.createElement(InputGroup.Text, {
    as: "label",
    for: expression.expressionType.toLowerCase() + '-answer-' + index
  }, expression.expressionType === TERM ? /*#__PURE__*/React.createElement(React.Fragment, null, EXPRESSION_LABEL[expression.expressionType], /*#__PURE__*/React.createElement("sub", null, index + 1), /*#__PURE__*/React.createElement("sup", null, "\uD835\uDCDC"), "[\uD835\uDC52] =") : '𝓜')), /*#__PURE__*/React.createElement(Form.Control, {
    as: "select",
    value: item.answerValue,
    onChange: e => props.setExpressionAnswer(expression.expressionType, e.target.value, index),
    id: expression.expressionType.toLowerCase() + '-answer-' + index,
    disabled: item.answerLocked,
    className: "custom-select"
  }, expression.answers(props.domain)), expression.expressionType === TERM ? null : /*#__PURE__*/React.createElement(InputGroup.Append, null, /*#__PURE__*/React.createElement(InputGroup.Text, {
    id: expression.expressionType.toLowerCase() + '-answer-' + index
  }, "\uD835\uDF4B", /*#__PURE__*/React.createElement("sub", null, index + 1), "[\uD835\uDC52]")), props.teacherMode ? /*#__PURE__*/React.createElement(InputGroup.Append, null, /*#__PURE__*/React.createElement(LockButton, {
    lockFn: () => props.lockExpressionAnswer(expression.expressionType, index),
    locked: item.answerLocked
  })) : null)), /*#__PURE__*/React.createElement(Col, {
    className: "pr-0 text-nowrap mb-1"
  }, item.answerValue !== '' ? item.answerValue === item.expressionValue ? /*#__PURE__*/React.createElement("strong", {
    className: "text-success pr-0"
  }, /*#__PURE__*/React.createElement(FontAwesome, {
    name: "check"
  }), /*#__PURE__*/React.createElement("span", {
    className: "d-none d-sm-inline"
  }, "\xA0Correct")) : /*#__PURE__*/React.createElement("strong", {
    className: "text-danger"
  }, /*#__PURE__*/React.createElement(FontAwesome, {
    name: "times"
  }), /*#__PURE__*/React.createElement("span", {
    className: "d-none d-sm-inline"
  }, "\xA0Incorrect")) : null), /*#__PURE__*/React.createElement(Col, {
    className: "pr-0 mb-1"
  }, expression.expressionType === FORMULA ? /*#__PURE__*/React.createElement(HenkinHintikkaGameButton, {
    onClick: () => props.initiateGame(index),
    gameEnabled: item.gameEnabled,
    disabled: item.errorMessage.length > 0 || item.value === ''
  }) : null)), item.gameEnabled && item.errorMessage.length === 0 ? /*#__PURE__*/React.createElement(HenkinHintikkaGameContainer, {
    index: index,
    formula: item,
    domain: props.domain
  }) : null));
}

export default Expressions;